import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useStudentSignUp } from '../hooks/useStudentSignUp';
import CustomTextInput from '../../../shared/components/ui/CustomTextInput';
import AppButton from '../../../shared/components/ui/AppButton';
import AppDropDownPicker from '../../../shared/components/ui/AppDropDownPicker';
import PageTemplate from '../../../shared/components/layout/PageTemplate';
import TermsTemplate from '../../../screens/TermsTemplate/TermsTemplate';
import { clearCourseData } from '../../../store/slices/courseSlice';
import { resetTutorData } from '../../../store/slices/tutorSlice';
import * as S from '../styles/StudentSignUp.styles';
import * as FormStyles from '../../../shared/styles/form.styles';

const backIcon = require('../../../assets/icons/back.png');

const logo = require('../../../assets/logo-no-padding.png');

const StudentSignUp: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showTerms, setShowTerms] = useState(false);
  
  const handleLogoClick = () => {
    navigate('/');
  };
  
  const {
    firstName,
    lastName,
    email,
    password,
    phone,
    grade,
    school,
    errors,
    setFirstName,
    setLastName,
    setEmail,
    setPassword,
    setPhone,
    setGrade,
    setSchool,
    handleSubmit,
    isLoading,
    schoolDropdownData,
    gradeDropdownData,
    setTerms,
    terms
  } = useStudentSignUp();

  const handleShowTerms = () => {
    setShowTerms(true);
  };

  const handleAcceptTerms = () => {
    setTerms(true);
    setShowTerms(false);
  };

  const handleDeclineTerms = () => {
    setTerms(false);
    setShowTerms(false);
  };

  const handleBackFromTerms = () => {
    setShowTerms(false);
  };

  const handleBack = () => {
    // Clear Redux state when back is pressed from student enrollment
    dispatch(clearCourseData());
    dispatch(resetTutorData());
    // Navigate back to student login page
    navigate('/login/student');
  };

  if (showTerms) {
    return (
      <TermsTemplate
        title="📄 STUDENT TERMS AND CONDITIONS & PRIVACY POLICY"
        platformName="Illuminate"
        contactEmail="illuminateusapp@gmail.com"
        onAccept={handleAcceptTerms}
        onBack={handleBackFromTerms}
        requireCheckbox={true}
        checkboxLabel="I have read and agree to the Student Terms and Conditions"
        buttonText="Accept"
        onDecline={handleDeclineTerms}
        sections={[
          {
            id: 'acceptance',
            title: '1. Acceptance of Terms',
            content: (
              <p>
                By accessing or using the Illuminate website or mobile app (the "Service"), you agree to comply with and be legally bound by these Terms and our Privacy Policy. If you disagree with any part, do not use the Service.
              </p>
            )
          },
          {
            id: 'service-description',
            title: '2. Description of Service',
            content: (
              <>
                <p>
                  Illuminate is a platform that connects students with vetted tutors for academic assistance. We provide tools for account creation, tutor discovery, communication, scheduling, and profile management.
                </p>
                <p>
                  We do not process or handle payments. Financial transactions, if any, occur off-platform between students (or their guardians) and tutors.
                </p>
              </>
            )
          },
          {
            id: 'eligibility',
            title: '3. Eligibility',
            content: (
              <p>
                You must be at least <strong>13 years old</strong> to create an account. If under 18, you may only use the Service with <strong>parental or legal guardian consent</strong>. We may require parent/guardian contact information for account approval.
              </p>
            )
          },
          {
            id: 'required-info',
            title: '4. Required Information',
            content: (
              <>
                <p>
                  To use the platform, users must provide <strong>truthful, accurate, and complete information</strong>, including:
                </p>
                <ul>
                  <li>Full legal name</li>
                  <li>Contact information (email, phone number)</li>
                  <li>Home address</li>
                  <li>Parent/guardian name and contact info (if a minor)</li>
                  <li>Academic records (e.g., school name, transcript)</li>
                </ul>
                <p>
                  Misrepresentation or submission of false documents is grounds for immediate termination.
                </p>
              </>
            )
          },
          {
            id: 'student-responsibilities',
            title: '5. Student Responsibilities',
            content: (
              <>
                <p>As a student using our platform, you agree to:</p>
                <ul>
                  <li>Use tutoring services for legitimate academic assistance</li>
                  <li>Maintain academic integrity and avoid cheating</li>
                  <li>Treat tutors with respect and professionalism</li>
                  <li>Provide accurate information about your academic needs</li>
                  <li>Follow your school's policies regarding outside tutoring</li>
                </ul>
                <p>
                  Students are expected to engage honestly in the learning process and not use our platform to circumvent academic requirements.
                </p>
              </>
            )
          },
          {
            id: 'parental-involvement',
            title: '6. Parental Involvement for Minors',
            content: (
              <>
                <p>For students under 18, parents or legal guardians must:</p>
                <ul>
                  <li>Provide consent for account creation</li>
                  <li>Monitor tutoring arrangements and communications</li>
                  <li>Handle all payment arrangements with tutors</li>
                  <li>Ensure compliance with these terms</li>
                </ul>
                <p>
                  Parents retain full responsibility for their minor's use of the platform and any agreements made with tutors.
                </p>
              </>
            )
          },
          {
            id: 'payments',
            title: '7. Payments and Financial Agreements',
            content: (
              <>
                <p>
                  Illuminate does <strong>not process or facilitate</strong> payments of any kind. We:
                </p>
                <ul>
                  <li>Do not monitor or track financial agreements</li>
                  <li>Do not issue invoices or receipts</li>
                  <li>Are not responsible for payment disputes, fraud, or refunds</li>
                </ul>
                <p>
                  All payment arrangements must be handled directly between student (or guardian) and tutor.
                </p>
              </>
            )
          },
          {
            id: 'conduct',
            title: '8. Conduct Expectations',
            content: (
              <>
                <p>Students agree to act respectfully and responsibly. Specifically, students may not:</p>
                <ul>
                  <li>Misrepresent academic needs or background</li>
                  <li>Share fake or altered academic documents</li>
                  <li>Harass or disrespect tutors</li>
                  <li>Use the Service to cheat or violate academic integrity</li>
                  <li>Share login credentials with others</li>
                </ul>
                <p>
                  Students are expected to engage in honest academic collaboration with their tutors.
                </p>
              </>
            )
          },
          {
            id: 'termination',
            title: '9. Termination',
            content: (
              <>
                <p>We reserve the right to suspend or permanently remove accounts for:</p>
                <ul>
                  <li>Violating these Terms</li>
                  <li>Submitting false information</li>
                  <li>Using the platform for unauthorized purposes</li>
                  <li>Failing to comply with community standards</li>
                </ul>
              </>
            )
          },
          {
            id: 'liability',
            title: '10. Limitation of Liability',
            content: (
              <>
                <p>Illuminate is <strong>not liable</strong> for:</p>
                <ul>
                  <li>Payment or service disputes between users</li>
                  <li>Academic performance results or tutoring effectiveness</li>
                  <li>Off-platform interactions or agreements</li>
                  <li>Any parental oversight failures</li>
                  <li>Any breach of these Terms by a tutor or student</li>
                </ul>
              </>
            )
          },
          {
            id: 'indemnification',
            title: '11. Indemnification',
            content: (
              <p>
                You agree to indemnify and hold harmless Illuminate, its operators, and affiliates from any claims, losses, or damages arising from your use of the Service, including disputes with other users and off-platform issues.
              </p>
            )
          },
          {
            id: 'governing-law',
            title: '12. Governing Law',
            content: (
              <p>
                These Terms are governed by the laws of the United States of America. Any disputes shall be resolved exclusively in the courts of that jurisdiction.
              </p>
            )
          },
          {
            id: 'changes',
            title: '13. Changes to Terms',
            content: (
              <p>
                We may update these Terms. Material changes will be notified through email or platform updates. Continued use of the platform indicates acceptance.
              </p>
            )
          }
        ]}
      />
    );
  }

  return (
    <PageTemplate logoSrc={logo} logoAlt="Illuminate" onLogoClick={handleLogoClick}>
      <FormStyles.ContainerTitle>Student Enrollment</FormStyles.ContainerTitle>
      <FormStyles.InstructionText>Fill in all the fields with required details.</FormStyles.InstructionText>
      <FormStyles.LayoutSideBySide>
        <CustomTextInput
          value={firstName}
          placeholder="First Name"
          onChangeText={setFirstName}
          error={errors.firstName}
        />
        <CustomTextInput
          value={lastName}
          placeholder="Last Name"
          onChangeText={setLastName}
          error={errors.lastName}
        />
      </FormStyles.LayoutSideBySide>

      <AppDropDownPicker
        multiple={false}
        items={schoolDropdownData}
        placeholder="Select School"
        value={school}
        onValueChange={v => setSchool(v?.toString() || '')}
        error={errors.school}
      />
      <AppDropDownPicker
        items={gradeDropdownData}
        placeholder="Grade"
        value={grade}
        onValueChange={v => setGrade(v?.toString() || '')}
        error={errors.grade}
      />
      <CustomTextInput
        value={phone}
        placeholder="Phone"
        onChangeText={setPhone}
        maxLength={14}
        type="tel"
        error={errors.phone}
      />
      <CustomTextInput
        value={email}
        placeholder="Email"
        onChangeText={setEmail}
        type="email"
        error={errors.email}
      />
      <CustomTextInput
        value={password}
        placeholder="Password"
        onChangeText={setPassword}
        type="password"
        secureTextEntry
        error={errors.password}
      />

      <div style={{ marginBottom: '20px' }}>
        <AppButton
          title={terms ? "✓ Terms Accepted" : "View Terms & Conditions"}
          onClick={handleShowTerms}
          style={{ 
            backgroundColor: terms ? '#28a745' : '#9282CB',
            width: '100%',
            marginBottom: '10px'
          }}
        />
      </div>

      <FormStyles.ButtonRow>
        <AppButton
          className="bttn-back"
          title="Back"
          onClick={handleBack}
          leftIcon={backIcon}
        />
        <AppButton
          className="bttn-next"
          title="Register"
          onClick={handleSubmit}
          disabled={!terms || isLoading}
        />
      </FormStyles.ButtonRow>
    </PageTemplate>
  );
};

export default StudentSignUp;
